import { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactDOM from "react-dom";
import api from "../axiosSetup";
import moment from "moment";
import DataTable from "./DataTable";
import { FaKey, FaFileCsv } from "react-icons/fa";
import cfg from "../../config";

const MemberList = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Passwort-Reset Modal
  const [resetModal, setResetModal] = useState(null); // { id, name }
  const [newPassword, setNewPassword] = useState("");
  const [mustChange, setMustChange] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  const [resetMsg, setResetMsg] = useState("");
  const [resetError, setResetError] = useState("");

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await api.get("/api/all-member-zunftrat", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMembers(res.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  const openResetModal = (member) => {
    const name = [member.firstName, member.lastName, member.Vorname, member.Nachname]
      .filter(Boolean).slice(0, 2).join(' ') || `ID ${member.ID || member.id}`;
    setResetModal({ id: member.ID || member.id, name });
    setNewPassword("");
    setMustChange(false);
    setResetMsg("");
    setResetError("");
  };

  const closeResetModal = () => {
    setResetModal(null);
    setNewPassword("");
    setMustChange(false);
    setResetMsg("");
    setResetError("");
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetMsg("");
    setResetSaving(true);
    try {
      const res = await api.post(`/api/admin/member/${resetModal.id}/reset-password`, {
        newPassword,
        mustChangePassword: mustChange,
      });
      setResetMsg(res.data.message || "Passwort erfolgreich zurückgesetzt.");
      setNewPassword("");
    } catch (err) {
      setResetError(err.response?.data?.message || "Fehler beim Zurücksetzen.");
    }
    setResetSaving(false);
  };

  const formatValue = (val) => {
    if (val == null) return null;
    if (typeof val === "boolean" || val === 1 || val === 0) return val;
    const s = String(val);
    if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(s) && moment(s).isValid()) {
      return moment(s).format("DD.MM.YYYY");
    }
    return val;
  };

  // Die Schnittstelle liefert neutrale Feldnamen; die Beschriftung entsteht
  // hier, damit die Rollenspalte dem Begriff aus tblconfig folgt.
  const BESCHRIFTUNG = useMemo(() => ({
    isActivated: 'Aktiviert',
    isCouncil:   cfg.adminRoleName,
  }), []);

  const columns = useMemo(() => {
    if (!members.length) return [];
    return Object.keys(members[0]).map((key) => ({
      key,
      label: BESCHRIFTUNG[key] ?? key,
      render: (val) => {
        const v = formatValue(val);
        if (v == null) return <span className="dt-muted">—</span>;
        if (v === true || v === 1) return <span className="dt-badge dt-badge-yes">Ja</span>;
        if (v === false || v === 0) return <span className="dt-badge dt-badge-no">Nein</span>;
        return String(v);
      },
      searchValue: (val) => (val == null ? "" : String(val)),
    }));
  }, [members]);

  const actions = (member) => (
    <div className="dt-actions">
      <button className="dt-btn dt-btn-primary" onClick={() => navigate("/zr-member-create", { state: { member } })}>
        Bearbeiten
      </button>
      <button className="dt-btn dt-btn-warning" onClick={() => openResetModal(member)} title="Passwort zurücksetzen" aria-label="Passwort zurücksetzen">
        <FaKey />
      </button>
    </div>
  );

  const handleCsvExport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const res = await api.get("/api/all-member-zunftrat/export-csv", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "mitglieder.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert("CSV-Export fehlgeschlagen.");
    }
  };

  const headerActions = (
    <div className="d-flex gap-2">
      <button className="dt-btn dt-btn-success" onClick={() => navigate("/zr-member-create")}>
        + {cfg.memberTerm} erstellen
      </button>
      <button className="dt-btn dt-btn-secondary" onClick={handleCsvExport} title="Als CSV exportieren">
        <FaFileCsv className="me-1" /> CSV Export
      </button>
    </div>
  );

  return (
    <div className="container-fluid px-3 px-md-4 mt-4">
      {location.state?.message && (
        <div className="alert alert-success mb-3">{location.state.message}</div>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">{cfg.memberTermPlural} verwalten</h2>
      </div>
      <DataTable
        data={members}
        columns={columns}
        loading={loading}
        error={error}
        actions={actions}
        headerActions={headerActions}
        searchPlaceholder={`${cfg.memberTerm} suchen…`}
        emptyText={`Keine ${cfg.memberTermPlural} gefunden.`}
        initialPageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
      />
      <br />

      {/* Passwort-Reset Modal — via Portal direkt in document.body */}
      {resetModal && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 99999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            background: '#212529', border: '1px solid #495057', borderRadius: '0.75rem',
            width: '100%', maxWidth: 460, color: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #495057' }}>
              <h5 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaKey style={{ color: '#facc15' }} />
                Passwort zurücksetzen
              </h5>
              <button onClick={closeResetModal} style={{ background: 'none', border: 'none', color: '#adb5bd', fontSize: '1.25rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {/* Body */}
            <form onSubmit={handleResetSubmit}>
              <div style={{ padding: '1.25rem' }}>
                <p style={{ color: '#adb5bd', marginBottom: '1rem' }}>
                  {cfg.memberTerm}: <strong style={{ color: '#fff' }}>{resetModal.name}</strong>
                </p>

                {resetMsg && <div className="alert alert-success py-2">{resetMsg}</div>}
                {resetError && <div className="alert alert-danger py-2">{resetError}</div>}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                    Neues Passwort <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control bg-dark text-white border-secondary"
                    placeholder="Mindestens 6 Zeichen"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    autoComplete="new-password"
                    autoFocus
                  />
                  <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.3rem' }}>
                    Dieses Passwort teilst du der Person direkt mit.
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    id="mustChange"
                    checked={mustChange}
                    onChange={e => setMustChange(e.target.checked)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <label htmlFor="mustChange" style={{ cursor: 'pointer', margin: 0, fontSize: '0.9rem' }}>
                    Passwort muss bei der nächsten Anmeldung geändert werden
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '1rem 1.25rem', borderTop: '1px solid #495057' }}>
                <button type="button" className="btn btn-outline-secondary" onClick={closeResetModal}>
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-warning fw-semibold" disabled={resetSaving}>
                  {resetSaving ? "Wird gesetzt…" : "Passwort setzen"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default MemberList;
